import { createContext, useContext, useMemo, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import type { AuthSession } from '../../../../types/plans'
import type { ConstructorPlan } from '../constructorTypes'

import type { DiagnosticsAiProposal } from '../diagnostics/planDiagnosticsCompose'

export type DiagnosticsDraftApplyHandler = (data: unknown) => void | Promise<void>

type DiagnosticsDraftContextValue = {
  plan: ConstructorPlan | null
  setPlan: Dispatch<SetStateAction<ConstructorPlan | null>>
  proposal: DiagnosticsAiProposal | null
  setProposal: Dispatch<SetStateAction<DiagnosticsAiProposal | null>>
  misPatientId: string | null
  setMisPatientId: Dispatch<SetStateAction<string | null>>
  staffUserId: string
  setStaffUserId: Dispatch<SetStateAction<string>>
  bootstrap: TenantBootstrapModel | null
  setBootstrap: Dispatch<SetStateAction<TenantBootstrapModel | null>>
  session: AuthSession | null
  setSession: Dispatch<SetStateAction<AuthSession | null>>
  onApplied: (() => void) | null
  setOnApplied: Dispatch<SetStateAction<(() => void) | null>>
  onMisPlansRefresh: ((misPlanId: string | null) => void) | null
  setOnMisPlansRefresh: Dispatch<SetStateAction<((misPlanId: string | null) => void) | null>>
  applyHandler: DiagnosticsDraftApplyHandler | null
  setApplyHandler: Dispatch<SetStateAction<DiagnosticsDraftApplyHandler | null>>
}

const ConstructorDiagnosticsDraftContext = createContext<DiagnosticsDraftContextValue | null>(null)

export function ConstructorDiagnosticsDraftProvider({ children }: { children: ReactNode }) {
  const [plan, setPlan] = useState<ConstructorPlan | null>(null)
  const [proposal, setProposal] = useState<DiagnosticsAiProposal | null>(null)
  const [misPatientId, setMisPatientId] = useState<string | null>(null)
  const [staffUserId, setStaffUserId] = useState('')
  const [bootstrap, setBootstrap] = useState<TenantBootstrapModel | null>(null)
  const [session, setSession] = useState<AuthSession | null>(null)
  const [onApplied, setOnApplied] = useState<(() => void) | null>(null)
  const [onMisPlansRefresh, setOnMisPlansRefresh] = useState<((misPlanId: string | null) => void) | null>(null)
  const [applyHandler, setApplyHandler] = useState<DiagnosticsDraftApplyHandler | null>(null)

  const value = useMemo(
    () => ({
      plan,
      setPlan,
      proposal,
      setProposal,
      misPatientId,
      setMisPatientId,
      staffUserId,
      setStaffUserId,
      bootstrap,
      setBootstrap,
      session,
      setSession,
      onApplied,
      setOnApplied,
      onMisPlansRefresh,
      setOnMisPlansRefresh,
      applyHandler,
      setApplyHandler,
    }),
    [plan, proposal, misPatientId, staffUserId, bootstrap, session, onApplied, onMisPlansRefresh, applyHandler],
  )

  return (
    <ConstructorDiagnosticsDraftContext.Provider value={value}>{children}</ConstructorDiagnosticsDraftContext.Provider>
  )
}

export function useConstructorDiagnosticsDraft() {
  const ctx = useContext(ConstructorDiagnosticsDraftContext)
  if (!ctx) {
    throw new Error('useConstructorDiagnosticsDraft requires ConstructorDiagnosticsDraftProvider')
  }
  return ctx
}
